import { errorView, queryView, type QueryResultLike } from '@krp/client-runtime'

import { isOutcomeUnknown } from '../../../shared/model/feedback'
import { formatKopecks } from '../../../shared/model/format'
import { formatMoment } from '../../../shared/model/query'
import type {
  SlipRefundOutcomeVm,
  SlipRefundSale,
  SlipRefundSeat,
  SlipRefundSlip,
  SlipRefundSlips,
  SlipRefundVm,
  SlipRowVm,
} from './slipRefundTypes'

/** Предел суммы - как у службы (10^13 копеек): дальше - ошибка ввода, а не возврат. */
const MAX_KOPECKS = 10_000_000_000_000

/**
 * Сумма, введённая человеком, в копейках - без плавающей точки: «12», «12,5», «12.50». Больше двух знаков после запятой, ноль, минус,
 * пробелы внутри числа и буквы - не сумма (`null`).
 */
export function parseKopecks(text: string): number | null {
  const match = /^(\d{1,12})(?:[.,](\d{1,2}))?$/.exec(text.trim())
  if (match === null) return null
  const kopecks = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'))
  return kopecks > 0 && kopecks <= MAX_KOPECKS ? kopecks : null
}

/** Сумма для поля ввода: рубли и копейки через запятую, без символа валюты. */
export function kopecksInputText(kopecks: number): string {
  const rubles = Math.trunc(kopecks / 100)
  const rest = kopecks % 100
  return rest === 0 ? String(rubles) : `${rubles},${String(rest).padStart(2, '0')}`
}

/** Что сказать человеку, когда исход возврата неизвестен: повторять нельзя, исход выясняется чтением и ручной сверкой. */
export const UNKNOWN_NOTE =
  'Деньги могли быть возвращены. Повторять возврат нельзя: нажмите «Проверить статус» - окно прочитает операцию и остаток слипа, ничего не отправляя. ' +
  'Если статус не прояснится, сверьте возврат на терминале и в кабинете AQSI вручную.'

function slipRow(slip: SlipRefundSlip): SlipRowVm {
  return {
    id: slip.id,
    amountKopecks: slip.amountKopecks,
    amountText: formatKopecks(slip.amountKopecks),
    timeText: formatMoment(slip.processedAt),
    returnedText: formatKopecks(slip.returnedKopecks),
    availableKopecks: slip.availableKopecks,
    availableText: formatKopecks(slip.availableKopecks),
    hasSale: slip.hasSale,
    canRefund: slip.availableKopecks > 0,
  }
}

/** Ответ кабинета дословно: полный текст операции, иначе сообщение продажи (первая строка). */
function cabinetTextOf(sale: SlipRefundSale): string | null {
  const text = sale.operation?.providerMessage ?? sale.lastMessage
  return text === null || text.trim() === '' ? null : text
}

/** Итог по состоянию возврата на сервере. Успех - только подтверждённый кабинетом и завершённый; отказ - только доказанный. */
export function outcomeOfSale(sale: SlipRefundSale): SlipRefundOutcomeVm {
  const amount = formatKopecks(sale.totalKopecks)
  if (sale.payment === 'Succeeded' && sale.isFinished) {
    return {
      tone: 'success',
      title: 'Возврат выполнен: деньги возвращены на карту',
      facts: [`Сумма: ${amount}`, ...(sale.slipId === null ? [] : [`Слип возврата: ${sale.slipId}`]), 'Фискальный чек не пробивался.'],
      cabinetText: null,
      note: 'Чек коррекции оформите по порядку, принятому у вас: служба его не проверяет.',
      meta: null,
      mustNotRepeat: false,
    }
  }

  // Оформлен, но не отправлен (процесс остановился между созданием и отправкой): деньги не двигались, новый возврат отменит его сам.
  if (sale.payment === 'Pending' && sale.operation === null) {
    return {
      tone: 'failure',
      title: 'Возврат оформлен, но не отправлен: деньги не двигались',
      facts: [`Сумма: ${amount}`],
      cabinetText: null,
      note: 'Выберите слип и оформите возврат заново: прежний неотправленный возврат будет отменён сам.',
      meta: null,
      mustNotRepeat: false,
    }
  }

  if (sale.payment === 'Failed') {
    return {
      tone: 'failure',
      title: 'Возврат не выполнен: деньги не возвращались',
      facts: [`Сумма: ${amount}`],
      cabinetText: cabinetTextOf(sale) ?? 'Кабинет не назвал причину отказа',
      note: 'Можно выбрать слип и оформить возврат заново: это новое решение человека.',
      meta: null,
      mustNotRepeat: false,
    }
  }

  return {
    tone: 'unknown',
    title: 'Исход возврата неизвестен',
    facts: [
      `Сумма: ${amount}`,
      ...(sale.operation?.providerStatus == null ? [] : [`Статус кабинета: ${sale.operation.providerStatus}`]),
      ...(sale.operation?.manualRequired === true ? ['Нужна ручная сверка'] : []),
    ],
    cabinetText: cabinetTextOf(sale),
    note: UNKNOWN_NOTE,
    meta: null,
    mustNotRepeat: true,
  }
}

/** Итог по ошибке запроса: отказ службы - дословно и «деньги не возвращались»; нет ответа - исход неизвестен. */
export function outcomeOfError(error: unknown): SlipRefundOutcomeVm {
  const view = errorView(error)
  const meta = `Код: ${view.code} · запрос ${view.correlationId ?? '-'}`
  if (isOutcomeUnknown(error)) {
    return {
      tone: 'unknown',
      title: 'Ответ на возврат не получен: исход неизвестен',
      facts: [],
      cabinetText: view.message,
      note: UNKNOWN_NOTE,
      meta,
      mustNotRepeat: true,
    }
  }

  return {
    tone: 'failure',
    title: 'Возврат не выполнен: деньги не возвращались',
    facts: [],
    cabinetText: view.message,
    note: 'Исправьте причину и оформите возврат заново: это новое решение человека.',
    meta,
    mustNotRepeat: false,
  }
}

export interface SlipRefundVmInput {
  /** `null` - работа за кассой не начата. */
  seat: { deviceId: number } | null
  slips: QueryResultLike<SlipRefundSlips> | undefined
  send: { isLoading: boolean; isSuccess: boolean; isError: boolean; error?: unknown; data?: SlipRefundSale | undefined }
  seatState: SlipRefundSeat | undefined
}

const UNREAD: QueryResultLike<SlipRefundSlips> = { isUninitialized: true, isLoading: false, isError: false, isSuccess: false }

/**
 * Окно «Возврат по слипу»: слипы, ход отправки и итог. Итог берётся из самого свежего источника: открытый возврат на кассе (чтение), иначе ответ
 * на нажатие, иначе ошибка. Сверка исхода неизвестного возврата идёт только чтением; у окна нет ни повтора, ни второй отправки.
 */
export function buildSlipRefundVm(input: SlipRefundVmInput): SlipRefundVm {
  const view = queryView(input.slips ?? UNREAD, (data) => data.items.length === 0)
  const read = input.slips?.data
  const open = input.seatState?.kind === 'slip-refund' ? input.seatState.refund : null
  let outcome: SlipRefundOutcomeVm | null = null
  if (open !== null) outcome = outcomeOfSale(open)
  else if (input.send.isSuccess && input.send.data !== undefined) outcome = outcomeOfSale(input.send.data)
  else if (input.send.isError) outcome = outcomeOfError(input.send.error)
  const sending = input.send.isLoading
  return {
    seatMissing: input.seat === null,
    slipsStatus: view,
    slips: read === undefined || (view.status !== 'ready' && view.status !== 'empty') ? [] : read.items.map(slipRow),
    lookbackDays: read?.lookbackDays ?? 0,
    phase: sending ? 'sending' : outcome === null ? 'idle' : 'result',
    outcome: sending ? null : outcome,
    statusNote:
      outcome?.tone === 'unknown' && open === null && input.seatState?.kind === 'none'
        ? 'По последнему чтению на кассе нет открытого возврата по слипу. Если возврат был оформлен, он уже завершён: сверьте остаток слипа в списке.'
        : null,
    canStartOver: open === null || outcome?.tone !== 'unknown',
    busyReason:
      input.seatState?.kind === 'other'
        ? 'На кассе идёт другая операция (продажа или возврат по чеку): возврат по слипу можно начать, когда она будет завершена или отменена.'
        : null,
  }
}
